import { v2Api } from './v2Client';

const LOG = '[SquashAPI wallet]';

/**
 * Wallet joueur (crédit terrain) — voir Controllers/WalletController.cs et docs/WALLET.md côté API.
 * Lecture : staff (rôles 1 et 2). Ajustement manuel : administrateur (rôle 1) uniquement, 403 sinon.
 */

/** Libellés des mouvements (WalletEntry.kind). */
export const WALLET_ENTRY_KIND_LABELS = {
  membership_credit: 'Crédit abonnement',
  membership_credit_reversal: 'Reprise crédit abonnement',
  topup: 'Recharge par carte',
  topup_reversal: 'Recharge remboursée',
  reservation_payment: 'Réservation',
  reservation_refund: 'Réservation recréditée',
  booking_hold_release: 'Réservation non aboutie',
  shop_payment: 'Boutique',
  shop_refund: 'Boutique (crédit rendu)',
  admin_credit: 'Ajustement (crédit)',
  admin_debit: 'Ajustement (débit)',
};

export const WALLET_SOURCE_LABELS = {
  mobile: 'App',
  erp: 'ERP',
  webhook: 'Stripe',
  reconciler: 'Synchro auto',
  system: 'Système',
};

function normalizeEntry(e) {
  return {
    ...e,
    id: String(e.id),
    amount_cents: Number(e.amount_cents ?? 0) || 0,
    balance_after_cents: Number(e.balance_after_cents ?? 0) || 0,
    reservation_id: e.reservation_id != null ? String(e.reservation_id) : null,
    membership_subscription_id: e.membership_subscription_id != null ? String(e.membership_subscription_id) : null,
  };
}

/** GET /Wallet/admin/balances — soldes des joueurs. */
export async function listWalletBalances({ search, nonZeroOnly = true, page = 1, pageSize = 50 } = {}) {
  const params = { page, page_size: pageSize, non_zero_only: nonZeroOnly };
  if (search) params.search = search;
  try {
    const { data } = await v2Api.get('/Wallet/admin/balances', { params });
    return {
      items: (Array.isArray(data?.items) ? data.items : []).map((r) => ({
        ...r,
        player_id: String(r.player_id),
        balance_cents: Number(r.balance_cents ?? 0) || 0,
      })),
      total: data?.total ?? 0,
      totalBalanceCents: Number(data?.total_balance_cents ?? 0) || 0,
    };
  } catch (err) {
    console.error(LOG, 'balances échoué', { params, err });
    throw err;
  }
}

/** GET /Wallet/admin/player/{id} — solde + historique d'un joueur. */
export async function getPlayerWallet(playerId, { page = 1, pageSize = 50 } = {}) {
  try {
    const { data } = await v2Api.get(`/Wallet/admin/player/${encodeURIComponent(playerId)}`, { params: { page, page_size: pageSize } });
    return normalizePlayerWallet(data);
  } catch (err) {
    console.error(LOG, 'player échoué', { playerId, err });
    throw err;
  }
}

function normalizePlayerWallet(data) {
  return {
    playerId: data?.player_id != null ? String(data.player_id) : null,
    playerName: data?.player_name ?? '',
    balanceCents: Number(data?.balance_cents ?? 0) || 0,
    totalCreditedCents: Number(data?.total_credited_cents ?? 0) || 0,
    totalDebitedCents: Number(data?.total_debited_cents ?? 0) || 0,
    items: (Array.isArray(data?.items) ? data.items : []).map(normalizeEntry),
    total: data?.total ?? 0,
  };
}

/** POST /Wallet/admin/adjust — amountCents > 0 crédite, < 0 débite (jamais au-delà du solde). */
export async function adjustPlayerWallet({ playerId, amountCents, reason, staffNote, requestId }) {
  const payload = {
    player_id: String(playerId),
    amount_cents: amountCents,
    reason,
    staff_note: staffNote || null,
    request_id: requestId,
  };
  try {
    const { data } = await v2Api.post('/Wallet/admin/adjust', payload);
    return normalizePlayerWallet(data);
  } catch (err) {
    console.error(LOG, 'adjust échoué', { payload, err });
    throw err;
  }
}

export function walletErrorMessage(err, fallback) {
  return err?.response?.data?.message || err?.message || fallback;
}

export const walletService = {
  listBalances: listWalletBalances,
  getPlayer: getPlayerWallet,
  adjust: adjustPlayerWallet,
};
